import React from "react";
import { useApolloClient } from "@apollo/client";
import { casesActions } from "configs";
import {
  getImageUrl,
  useAuth,
  useCurrentCase,
  useDispatch,
  useOnline,
  useParams,
  useToast,
  getCaseImageIds
} from "duck";
import {
  GetFullMedicalCaseDocument,
  GetFullMedicalCaseQuery,
  GetFullMedicalCaseQueryVariables,
  GetMercuryUrlDocument,
  GetMercuryUrlQuery,
  GetMercuryUrlQueryVariables,
  MedicalCase
} from "requests";
import { isRequiredInfoFilled } from "./utils";

export const useCaseConfig = () =>
  useCurrentCase(medCase => ({
    hasCase: !!medCase,
    infoFilled: isRequiredInfoFilled(medCase?.data),
    hasDentalCharting: !!medCase?.data?.client?.patient?.generalAssessment,
    hasTplo: !!medCase?.data?.client?.patient
      ?.tibialPlateauLevelingOsteotomyPlanning,
    shouldRefresh: medCase ? medCase.shouldSync : true,
    normalStatus: medCase?.status === "normal"
  }));

export const useInitCaseData = (shouldRefresh: boolean) => {
  const { id } = useParams();
  const client = useApolloClient();
  const [auth] = useAuth();

  const online = useOnline();
  const dispatch = useDispatch();
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!shouldRefresh || !online) {
      setLoading(false);
      return;
    }

    dispatch(casesActions.removeCase(id));

    client
      .query<GetFullMedicalCaseQuery, GetFullMedicalCaseQueryVariables>({
        query: GetFullMedicalCaseDocument,
        variables: { caseId: +id },
        fetchPolicy: "no-cache"
      })
      .then(({ data }) => {
        if (data.medicalCase) {
          dispatch(
            casesActions.initOnline({
              userId: auth.user._id,
              case: data.medicalCase
            })
          );
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  return { loading };
};

export const useRealtime = ({ initComplete }: { initComplete: boolean }) => {
  const client = useApolloClient();
  const toast = useToast();
  const [{ user }] = useAuth();
  const online = useOnline();

  const { id } = useParams();
  const dispatch = useDispatch();
  const shouldSync = useCurrentCase(data => {
    return data ? data?.shouldSync : false;
  });

  React.useEffect(() => {
    if (!initComplete || !shouldSync || !online) {
      return;
    }

    let es: EventSource;

    const messageListener = (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      const newCase = data.medicalCase;
      if (newCase.initiatorUserId === user._id) {
        return;
      }

      dispatch(
        casesActions.mergeRealtimeUpdate({
          _id: id,
          data: newCase as MedicalCase
        })
      );
    };

    const errorListener = () => {
      toast({
        status: "error",
        description:
          "Realtime updates are not available. Please contact Admin of the system."
      });
      es.removeEventListener("message", messageListener);
      es.removeEventListener("error", errorListener);
    };

    client
      .query<GetMercuryUrlQuery, GetMercuryUrlQueryVariables>({
        query: GetMercuryUrlDocument,
        variables: { caseId: +id },
        fetchPolicy: "no-cache"
      })
      .then(({ data }) => {
        const mercureURL = data.medicalCase?.mercureUrl;
        if (!mercureURL) {
          return;
        }

        es = new EventSource(mercureURL);
        es.addEventListener("message", messageListener);
        es.addEventListener("error", errorListener);
      })
      .catch(() => {});

    return () => {
      if (es) {
        es.removeEventListener("message", messageListener);
        es.removeEventListener("error", errorListener);
      }
    };
  }, [shouldSync, initComplete, online]);
};

export const useCacheImages = ({ initComplete }: { initComplete: boolean }) => {
  const online = useOnline();

  const imageIds = useCurrentCase(state =>
    initComplete ? getCaseImageIds(state.data) : []
  );

  React.useEffect(() => {
    if (!online || !initComplete) {
      return;
    }

    imageIds.forEach(id =>
      fetch(getImageUrl(id), {
        headers: { "X-SHOULD-CACHE": "true" }
      }).catch(() => {})
    );
  }, [online, initComplete, imageIds]);
};
